import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import HomePage from "./pages/HomePage";
import SolutionsPage from "./pages/SolutionsPage";
import FeaturesAndPricingPage from "./pages/FeaturesAndPricingPage";
import ROICalculatorPage from "./pages/ROICalculatorPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import { CaseStudyListPage, CaseStudyPostPage } from "./pages/CaseStudiesPage";
import AIDemoPage from "./pages/AIDemoPage";
import BookingPage from "./pages/BookingPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsOfServicePage from "./pages/TermsOfServicePage";
import CookiePolicyPage from "./pages/CookiePolicyPage";
import PromoPopup from "./components/PromoPopup";
import LiveChatWidget from "./components/LiveChatWidget";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { CMSProvider } from "./context/CMSContext";
import { BookingProvider } from "./context/BookingContext";
import { Toaster } from "sonner";
import { usePageTracking } from "./hooks/usePageTracking";

// Admin Pages
import AdminLoginPage from "./pages/admin/AdminLoginPage";
import AdminLayout, { AdminDashboard } from "./pages/admin/AdminLayout";
import AdminHeroPage from "./pages/admin/AdminHeroPage";
import AdminTestimonialsPage from "./pages/admin/AdminTestimonialsPage";
import AdminFeaturesPage from "./pages/admin/AdminFeaturesPage";
import AdminPricingPage from "./pages/admin/AdminPricingPage";
import AdminFeaturesPricingPage from "./pages/admin/AdminFeaturesPricingPage";
import AdminCaseStudiesPage from "./pages/admin/AdminCaseStudiesPage";
import AdminSectionsPage from "./pages/admin/AdminSectionsPage";
import AdminImagesPage from "./pages/admin/AdminImagesPage";
import AdminAboutPage from "./pages/admin/AdminAboutPage";
import AdminSolutionsPage from "./pages/admin/AdminSolutionsPage";
import AdminFAQsPage from "./pages/admin/AdminFAQsPage";
import AdminAnalyticsPage from "./pages/admin/AdminAnalyticsPage";
import AdminBookingsPage from "./pages/admin/AdminBookingsPage";
import AdminCRMPage from "./pages/admin/AdminCRMPage";
import AdminSocialLinksPage from "./pages/admin/AdminSocialLinksPage";
import AdminROISettingsPage from "./pages/admin/AdminROISettingsPage";
import AdminContactSettingsPage from "./pages/admin/AdminContactSettingsPage";
import AdminAIDemoSettingsPage from "./pages/admin/AdminAIDemoSettingsPage";
import AdminAIFeaturesSettingsPage from "./pages/admin/AdminAIFeaturesSettingsPage";
import AdminLegalPagesPage from "./pages/admin/AdminLegalPagesPage";
import AdminSEOSettingsPage from "./pages/admin/AdminSEOSettingsPage";
import AdminABTestingPage from "./pages/admin/AdminABTestingPage";
import AdminPromoPopupPage from "./pages/admin/AdminPromoPopupPage";
import AdminUsersPage from "./pages/admin/AdminUsersPage";
import AdminBookingSettingsPage from "./pages/admin/AdminBookingSettingsPage";

// Scroll to top on route change
function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      // If there's a hash, scroll to the element
      const element = document.getElementById(hash.replace('#', ''));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    } else {
      // Otherwise scroll to top
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);

  return null;
}

// Page tracking wrapper
function PageTracker() {
  usePageTracking();
  return null;
}

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <CMSProvider>
          <BookingProvider>
            <BrowserRouter>
              <ScrollToTop />
              <PageTracker />
              <Toaster position="top-right" richColors />
              <PromoPopup />
              <LiveChatWidget />
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<HomePage />} />
                <Route path="/solutions" element={<SolutionsPage />} />
                <Route path="/features-pricing" element={<FeaturesAndPricingPage />} />
                <Route path="/roi-calculator" element={<ROICalculatorPage />} />
                {/* Redirect old routes to new combined page */}
                <Route path="/ai-features" element={<Navigate to="/features-pricing" replace />} />
                <Route path="/pricing" element={<Navigate to="/features-pricing" replace />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
              <Route path="/book" element={<BookingPage />} />
              <Route path="/booking" element={<BookingPage />} />
              
              {/* Legal Pages */}
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/terms-of-service" element={<TermsOfServicePage />} />
              <Route path="/cookie-policy" element={<CookiePolicyPage />} />
              
              {/* Case Studies Routes */}
              <Route path="/case-studies" element={<CaseStudyListPage />} />
              <Route path="/case-studies/:slug" element={<CaseStudyPostPage />} />
              {/* Redirect old blog routes */}
              <Route path="/blog" element={<Navigate to="/case-studies" replace />} />
              <Route path="/blog/:slug" element={<Navigate to="/case-studies" replace />} />
              
              {/* AI Demo */}
              <Route path="/ai-demo" element={<AIDemoPage />} />
              
              {/* Admin Routes */}
              <Route path="/admin/login" element={<AdminLoginPage />} />
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<Navigate to="/admin/dashboard" replace />} />
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="analytics" element={<AdminAnalyticsPage />} />
                <Route path="crm" element={<AdminCRMPage />} />
                <Route path="bookings" element={<AdminBookingsPage />} />
                <Route path="hero" element={<AdminHeroPage />} />
                <Route path="testimonials" element={<AdminTestimonialsPage />} />
                <Route path="features" element={<AdminFeaturesPage />} />
                <Route path="features-pricing" element={<AdminFeaturesPricingPage />} />
                <Route path="pricing" element={<AdminPricingPage />} />
                <Route path="case-studies" element={<AdminCaseStudiesPage />} />
                <Route path="about" element={<AdminAboutPage />} />
                <Route path="solutions" element={<AdminSolutionsPage />} />
                <Route path="faqs" element={<AdminFAQsPage />} />
                <Route path="social-links" element={<AdminSocialLinksPage />} />
                <Route path="roi-settings" element={<AdminROISettingsPage />} />
                <Route path="contact-settings" element={<AdminContactSettingsPage />} />
                <Route path="ai-demo-settings" element={<AdminAIDemoSettingsPage />} />
                <Route path="ai-features-settings" element={<AdminAIFeaturesSettingsPage />} />
                <Route path="legal-pages" element={<AdminLegalPagesPage />} />
                <Route path="seo-settings" element={<AdminSEOSettingsPage />} />
                <Route path="ab-testing" element={<AdminABTestingPage />} />
                <Route path="promo-popup" element={<AdminPromoPopupPage />} />
                <Route path="images" element={<AdminImagesPage />} />
                <Route path="sections" element={<AdminSectionsPage />} />
                <Route path="users" element={<AdminUsersPage />} />
                <Route path="booking-settings" element={<AdminBookingSettingsPage />} />
              </Route>
            </Routes>
          </BrowserRouter>
          </BookingProvider>
        </CMSProvider>
      </div>
    </HelmetProvider>
  );
}

export default App;
